import React from 'react';
import { IoIosArrowUp } from 'react-icons/io';
import { IoIosArrowDown } from 'react-icons/io';
import { IoMdCheckmark } from 'react-icons/io';
import columnToLetter from '../../../../../utils/columnToLetter';
import { TableLines } from './Lines';
import { LuTable2 } from 'react-icons/lu';
import { BiUndo, BiRedo } from 'react-icons/bi';
import { FiZoomIn } from 'react-icons/fi';
import { HiOutlineZoomOut } from 'react-icons/hi';
import { ActionIcon, CloseButton, Flex, Input, Switch, Text, Menu, Tooltip } from '@mantine/core';
import { IoIosSearch } from 'react-icons/io';
import { BsArrowsFullscreen } from 'react-icons/bs';
import { TbSnowflake } from 'react-icons/tb';
import { TbSnowflakeOff } from 'react-icons/tb';
import { TbSortAscending } from 'react-icons/tb';
import { TbSortDescending } from 'react-icons/tb';

export function Header({
  onFixRow,
  onFixColumn,
  focusedCell,
  isStructured,
  onSearch,
  foundItems,
  onSearchToUp,
  onSearchToDown,
  activeSearchedIndex,
  term,
  onFixRowAndColumn,
  fixedRowId,
  fixedColumnId,
  hideTableType,
  disableTableTypeToggle,
  onRedo,
  onUndo,
  isRedoDisabled,
  isUndoDisabled,
  onZoomIn,
  onZoomOut,
  onFullScreen,
  onBackFullScreen,
  isFullScreen,
  footerIndex,
  headerIndex,
  totalDataLength,
  onChangeHeaderFooter,
  onChangeTableStructure
}) {
  return (
    <Flex id="grid-table-header-root" align="center" justify="space-between" mb="16px">
      <Flex id="grid-table-header-left" align="center" gap="16px">
        <Flex id="grid-table-header-history-actions" align="center">
          <Tooltip label="Undo">
            <ActionIcon
              id="grid-table-header-undo"
              variant="subtle"
              onClick={onUndo}
              disabled={isUndoDisabled}
              bg="transparent"
              c="blue.6"
              opacity={isUndoDisabled && 0.5}
              aria-label="Undo"
            >
              <BiUndo fontSize="20px" />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Redo">
            <ActionIcon
              id="grid-table-header-redo"
              variant="subtle"
              onClick={onRedo}
              disabled={isRedoDisabled}
              bg="transparent"
              c="blue.6"
              opacity={isUndoDisabled && 0.5}
              aria-label="Redo"
            >
              <BiRedo fontSize="20px" />
            </ActionIcon>
          </Tooltip>
        </Flex>

        <Flex id="grid-table-header-zoom-actions" align="center">
          <Tooltip label="Zoom Out">
            <ActionIcon
              id="grid-table-header-zoom-out"
              variant="subtle"
              onClick={onZoomOut}
              aria-label="Zoom Out"
            >
              <HiOutlineZoomOut />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Zoom In">
            <ActionIcon
              id="grid-table-header-zoom-in"
              variant="subtle"
              onClick={onZoomIn}
              aria-label="Zoom In"
            >
              <FiZoomIn />
            </ActionIcon>
          </Tooltip>
        </Flex>
        <Flex id="grid-table-header-search" align="center" gap="6px">
          <Input
            id="grid-table-header-search-input"
            value={term}
            onChange={e => onSearch(e.target.value)}
            placeholder="Search"
            aria-label="Search"
            leftSection={<IoIosSearch />}
            size="xs"
            rightSectionPointerEvents="all"
            rightSection={
              term && (
                <Flex align="center" pr="30px" gap="10px">
                  <Text fz="12px" c="gray.7">
                    {foundItems.length > 0 ? activeSearchedIndex + 1 : 0}/{foundItems.length}
                  </Text>
                  <CloseButton
                    id="grid-table-header-search-clear"
                    size="xs"
                    onClick={() => onSearch('')}
                  />
                </Flex>
              )
            }
          />
          {foundItems.length > 0 && (
            <Flex id="grid-table-header-search-nav" gap="2px">
              <ActionIcon id="grid-table-header-search-up" variant="subtle" onClick={onSearchToUp}>
                <IoIosArrowUp />
              </ActionIcon>
              <ActionIcon
                id="grid-table-header-search-down"
                variant="subtle"
                onClick={onSearchToDown}
              >
                <IoIosArrowDown />
              </ActionIcon>
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex id="grid-table-header-right" align="center" gap="16px">
        {!hideTableType && (
          <Flex id="grid-table-header-structure" align="center" gap="12px">
            <Text id="grid-table-header-structure-label" fz="12px" c="dark.9">
              {isStructured ? 'Structured table' : 'Unstructured table'}
            </Text>
            <Switch
              id="grid-table-header-structure-toggle"
              aria-label="Toggle structure"
              checked={isStructured}
              disabled={disableTableTypeToggle}
              onChange={event => onChangeTableStructure(event.currentTarget.checked)}
            />
          </Flex>
        )}

        {isStructured && !hideTableType && (
          <TableLines
            idPrefix="grid-table-header-lines"
            max={totalDataLength}
            headerLine={headerIndex}
            footerLine={footerIndex}
            onChangeHeaderFooter={onChangeHeaderFooter}
          />
        )}

        {focusedCell && (
          <Menu id="grid-table-header-freeze-menu" shadow="md" width={200} withinPortal={false}>
            <Menu.Target>
              <Tooltip label="Freeze Panes">
                <ActionIcon id="grid-table-header-freeze-trigger" variant="subtle">
                  <LuTable2 />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>

            <Menu.Dropdown id="grid-table-header-freeze-dropdown" p="12px">
              <Menu.Item
                id="grid-table-header-unfreeze"
                onClick={() => {
                  onFixRowAndColumn(null, null);
                }}
                leftSection={<TbSnowflakeOff color="#ADB5BD" />}
              >
                Unfreeze Panes
              </Menu.Item>

              <Menu.Item
                id="grid-table-header-freeze-both"
                onClick={() => {
                  onFixRowAndColumn(focusedCell.rowId, focusedCell.columnId);
                }}
                rightSection={
                  fixedRowId !== 0 && fixedColumnId !== 0 && <IoMdCheckmark color="#ADB5BD" />
                }
                leftSection={<TbSnowflake color="#ADB5BD" />}
              >
                Freeze Panes
              </Menu.Item>
              <Menu.Item
                id="grid-table-header-freeze-row"
                onClick={() => {
                  onFixRow(focusedCell.rowId);
                  onFixColumn(null);
                }}
                rightSection={
                  fixedRowId !== 0 && !fixedColumnId && <IoMdCheckmark color="#ADB5BD" />
                }
                leftSection={<TbSortAscending color="#ADB5BD" />}
              >
                Up to row {focusedCell.rowId}
              </Menu.Item>
              <Menu.Item
                id="grid-table-header-freeze-column"
                onClick={() => {
                  onFixColumn(focusedCell.columnId);
                  onFixRow(null);
                }}
                rightSection={
                  fixedColumnId !== 0 && !fixedRowId && <IoMdCheckmark color="#ADB5BD" />
                }
                leftSection={<TbSortDescending color="#ADB5BD" />}
              >
                Up to column {columnToLetter(focusedCell.columnId)}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}

        <Tooltip label="Full Screen">
          <ActionIcon
            id="grid-table-header-fullscreen"
            variant="subtle"
            aria-label="Full Screen"
            isActive={isFullScreen}
            onClick={isFullScreen ? onBackFullScreen : onFullScreen}
          >
            <BsArrowsFullscreen fontSize="16px" />
          </ActionIcon>
        </Tooltip>
      </Flex>
    </Flex>
  );
}
